import React, { useCallback, useEffect, useState } from 'react';
import patientService from '../../../../../services/patient.service';
import { useEyeconxStore } from '../../../../../store/eyeconxStore';
import { confirmDelete, showError, showInfo, showSuccess } from '../../../../../utils/ecxSwal';
import AlternativeContactsDisplay from '../../../../common/AlternativeContactsDisplay';
import { getInvalidDetailTypeMeta } from '../../../invalid-details/subparts/invalidDetailsQuery';
import { PatientInvalidDetailsSkeleton } from '../subparts/PatientTabSkeletons';
import PatientDetailIcon from '../subparts/PatientDetailIcon';

const PatientInvalidDetails = ({ patient }) => {
    const clinicId = useEyeconxStore((s) => s.user.clinicId);

    const [loading, setLoading] = useState(true);
    const [logs, setLogs] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [selectAll, setSelectAll] = useState(false);
    const [notFound, setNotFound] = useState(false);

    const fetchInvalidLogs = useCallback(async () => {
        if (!clinicId || !patient?.id) {
            return;
        }

        setLoading(true);
        setSelected(new Set());
        setSelectAll(false);

        try {
            const res = await patientService.getPatientInvalidData(clinicId, patient.id);
            setLogs(res.data || []);
            setNotFound(false);
        } catch (err) {
            if (err?.response?.status === 404) {
                setNotFound(true);
                setLogs([]);
            } else {
                showError({ title: 'Load Failed', text: 'Failed to load invalid detail logs.' });
            }
        } finally {
            setLoading(false);
        }
    }, [clinicId, patient?.id]);

    useEffect(() => {
        fetchInvalidLogs();
    }, [fetchInvalidLogs]);

    const toggleSelect = (id) => {
        const updated = new Set(selected);

        if (updated.has(id)) {
            updated.delete(id);
        } else {
            updated.add(id);
        }

        setSelected(updated);
    };

    const toggleSelectAll = () => {
        if (selectAll) {
            setSelected(new Set());
            setSelectAll(false);
        } else {
            setSelected(new Set(logs.map((l) => l.id)));
            setSelectAll(true);
        }
    };

    const deleteSingle = async (id) => {
        const result = await confirmDelete({
            title: 'Delete Invalid Detail?',
            itemName: 'this invalid detail record',
            description: 'Removed records will no longer appear in the invalid details log.',
            onConfirm: () => patientService.deletePatientInvalidData(clinicId, patient?.id, [id]),
        });

        if (result.isConfirmed) {
            showSuccess({ title: 'Deleted', text: 'Invalid detail removed.' });
            fetchInvalidLogs();
        }
    };

    const deleteSelected = async () => {
        if (selected.size === 0) {
            showInfo({ title: 'No Selection', text: 'Select one or more logs to delete.' });
            return;
        }

        const ids = Array.from(selected);
        const isBulk = ids.length > 1;

        const result = await confirmDelete({
            title: isBulk ? 'Delete Invalid Details?' : 'Delete Invalid Detail?',
            itemName: isBulk ? `${ids.length} invalid detail records` : 'this invalid detail record',
            description: 'Removed records will no longer appear in the invalid details log.',
            onConfirm: () => patientService.deletePatientInvalidData(clinicId, patient?.id, ids),
        });

        if (result.isConfirmed) {
            showSuccess({
                title: 'Deleted',
                text: isBulk ? 'Selected logs were removed.' : 'Invalid detail removed.',
            });
            fetchInvalidLogs();
        }
    };

    const getTypeLabel = (type) => {
        const meta = getInvalidDetailTypeMeta(type);

        switch (Number(type)) {
            case 1:
                return { label: meta.label, variant: 'danger', icon: meta.icon };
            case 2:
                return { label: meta.label, variant: 'warning', icon: meta.icon };
            case 3:
                return { label: meta.label, variant: 'secondary', icon: meta.icon };
            default:
                return { label: meta.label, variant: 'dark', icon: meta.icon };
        }
    };

    if (loading) {
        return <PatientInvalidDetailsSkeleton />;
    }

    return (
        <div className="patient-detail-tab-panel">
            <div className="patient-detail-tab-panel__header">
                <div>
                    <h3 className="patient-detail-tab-panel__title">
                        <PatientDetailIcon icon="bi-exclamation-octagon" theme="danger" size="sm" />
                        Invalid Patient Details
                    </h3>
                    <p className="patient-detail-tab-panel__subtitle">
                        Review invalid contact information and use alternative details from the patient file to reach out.
                    </p>
                </div>

                {logs.length > 0 && (
                    <div className="d-flex gap-2">
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={deleteSelected}
                        >
                            <i className="bi bi-trash me-1" />
                            Delete Selected
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-light-primary"
                            onClick={fetchInvalidLogs}
                        >
                            <i className="bi bi-arrow-clockwise me-1" />
                            Refresh
                        </button>
                    </div>
                )}
            </div>

            <div className="patient-detail-table-wrap">
                {notFound || logs.length === 0 ? (
                    <div className="patient-detail-empty-state">
                        <PatientDetailIcon icon="bi-check-circle" theme="success" size="lg" />
                        <p>No invalid details found.</p>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0 patient-detail-table">
                            <thead>
                                <tr>
                                    <th className="ps-4" scope="col">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            checked={selectAll}
                                            onChange={toggleSelectAll}
                                        />
                                    </th>
                                    <th scope="col">Type</th>
                                    <th scope="col">Invalid Detail</th>
                                    <th scope="col">Alternative Contacts</th>
                                    <th scope="col">Date</th>
                                    <th scope="col">Actions</th>
                                </tr>
                            </thead>

                            <tbody>
                                {logs.map((log) => {
                                    const info = getTypeLabel(log.type);
                                    return (
                                        <tr key={log.id}>
                                            <td className="ps-4">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={selected.has(log.id)}
                                                    onChange={() => toggleSelect(log.id)}
                                                />
                                            </td>

                                            <td>
                                                <span className={`badge bg-${info.variant}`}>
                                                    <i className={`bi ${info.icon} me-1`}></i>
                                                    {info.label}
                                                </span>
                                            </td>

                                            <td>
                                                <strong>{log.detail}</strong>
                                            </td>

                                            <td>
                                                <AlternativeContactsDisplay
                                                    contacts={log.alternativeContacts}
                                                    emptyLabel="None on file"
                                                />
                                            </td>

                                            <td>{log.createdAt}</td>

                                            <td className="pe-4">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => deleteSingle(log.id)}
                                                >
                                                    <i className="bi bi-trash" />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PatientInvalidDetails;
